import { lumi } from '../lib/lumi'

export type AuditAction = 
  | 'user_login'
  | 'user_logout'
  | 'profile_created'
  | 'profile_updated'
  | 'request_created'
  | 'request_updated'
  | 'donation_confirmed'
  | 'match_created'
  | 'lgpd_consent_given'
  | 'lgpd_data_deletion'
  | 'email_sent'
  | 'error_occurred'

interface AuditLogData {
  action: AuditAction
  userId?: string
  entityType?: string
  entityId?: string
  details?: string
  ipAddress?: string
  userAgent?: string
  success: boolean
  errorMessage?: string
}

export async function logAudit(data: AuditLogData) {
  try {
    const timestamp = new Date().toISOString()
    
    await lumi.entities.audit_logs.create({
      action: data.action,
      userId: data.userId || 'anonymous',
      entityType: data.entityType || '',
      entityId: data.entityId || '',
      details: data.details || '',
      ipAddress: data.ipAddress || 'unknown',
      userAgent: data.userAgent || navigator.userAgent,
      success: data.success,
      errorMessage: data.errorMessage || '',
      timestamp,
      creator: data.userId || 'system',
      createdAt: timestamp,
      updatedAt: timestamp
    })
  } catch (error) {
    // Silent fail - don't break app if logging fails
    console.error('Failed to log audit:', error)
  }
}

// Helper functions for common audit logs
export const auditHelpers = {
  logLogin: (userId: string) => logAudit({
    action: 'user_login',
    userId,
    success: true,
    details: 'User logged in successfully'
  }),

  logProfileCreated: (userId: string, profileId: string) => logAudit({
    action: 'profile_created',
    userId,
    entityType: 'profile',
    entityId: profileId,
    success: true,
    details: 'User profile created'
  }),

  logRequestCreated: (userId: string | undefined, requestId: string, isAnonymous: boolean) => logAudit({
    action: 'request_created',
    userId: userId || 'anonymous',
    entityType: 'request',
    entityId: requestId,
    success: true,
    details: isAnonymous ? 'Anonymous request created' : 'Request created by registered user'
  }),

  logDonationConfirmed: (userId: string, requestId: string) => logAudit({
    action: 'donation_confirmed',
    userId,
    entityType: 'request',
    entityId: requestId,
    success: true,
    details: 'Donation confirmed by donor'
  }),

  logLGPDConsent: (userId: string) => logAudit({
    action: 'lgpd_consent_given',
    userId,
    success: true,
    details: 'User gave LGPD consent'
  }),

  logError: (action: AuditAction, userId: string | undefined, error: string) => logAudit({
    action: 'error_occurred',
    userId: userId || 'anonymous',
    success: false,
    errorMessage: error,
    details: `Error during ${action}`
  })
}
